<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="destroy"></div>
            <div class="modal-content">
                <div class="popup-dialog">

                    <div class="popup-title">
                        <h1 class="title is-1">
                            {{ $t(`developers.webhooks.form.title${webhookId ? '_edit' : ''}`) }}
                        </h1>

                        <div class="popup-close" @click.prevent="destroy">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <g-form :action="formEndpoint"
                            :method="formMethod"
                            :form="form"
                            :before="formatData"
                            :prepopulate="prepopulate"
                            @prepopulated="prepopulated"
                            @success="formSuccess"
                            message="Wijzigingen succesvol opgeslagen">

                        <div class="popup-body popup-rows">
                            <div class="columns">
                                <div class="column is-one-third">
                                    <label>{{ $t('developers.webhooks.form.name') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body">
                                        <g-input name="name" :label="$t('developers.webhooks.form.name')"/>
                                    </div>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column is-one-third">
                                    <label>{{ $t('developers.webhooks.form.url') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body">
                                        <g-input name="endpoint" :label="$t('developers.webhooks.form.url_description')"/>
                                    </div>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column is-one-third">
                                    <label>{{ $t('developers.webhooks.form.events') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body" id="webhook-select">
                                        <tree-select :multiple="true"
                                                     :placeholder="$t('developers.webhooks.form.select_placeholder')"
                                                     :options="eventOptions"
                                                     :clearable="false"
                                                     value-consists-of="LEAF_PRIORITY"
                                                     v-model="form.events"/>
                                    </div>
                                </div>
                            </div>

                            <div class="columns" v-if="!webhookId">
                                <div class="column is-one-third">
                                    <label>{{ $t('developers.webhooks.form.secret') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body">
                                        <g-input name="secret" :label="$t('developers.webhooks.form.secret')"/>
                                    </div>

                                    <small class="helper-text" @click="form.secret = generateSecret()">
                                        <i class="icon">refresh</i>
                                        <span>{{ $t('developers.webhooks.form.regenerate_secret') }}</span>
                                    </small>

                                    <div class="mt-2">
                                        <small class="helper-text subtitle">
                                            {{ $t('developers.webhooks.form.secret_description') }}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column is-one-third">
                                    <label>{{ $t('developers.webhooks.form.notifications_enabled') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body mt-4">
                                        <g-switch name="notifications_enabled"/>
                                    </div>

                                    <div class="mt-2">
                                        <small class="helper-text subtitle">
                                            {{ $t('developers.webhooks.form.notifications_enabled_subtitle') }}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column is-one-third">
                                    <label>{{ $t('developers.webhooks.form.active') }}</label>
                                </div>

                                <div class="column is-vcentered">
                                    <div class="field-body">
                                        <g-switch name="status"/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="popup-footer">
                            <a @click.prevent="destroy" href="#" role="button">{{ $t('developers.webhooks.form.cancel') }}</a>
                            <g-button>{{ $t('developers.webhooks.form.save') }}</g-button>
                        </div>
                    </g-form>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    data() {
        const formData = {
            name: null,
            endpoint: null,
            events: [],
            secret: null,
            status: 1,
            notifications_enabled: 1,
        }

        return {
            form: new Form(formData),
            eventOptions: [
                {
                    id: 'giftcards',
                    label: 'Giftcards',
                    children: [
                        {
                            id: 'giftcard.issued',
                            label: 'giftcard.issued',
                        },
                        {
                            id: 'giftcard.redeemed',
                            label: 'giftcard.redeemed',
                        },
                        {
                            id: 'giftcard.expired',
                            label: 'giftcard.expired',
                        },
                        {
                            id: 'giftcard.extended',
                            label: 'giftcard.extended',
                        },
                        {
                            id: 'giftcard.topped_up',
                            label: 'giftcard.topped_up',
                        },
                    ]
                },
                {
                    id: 'transactions',
                    label: 'Transactions',
                    children: [
                        {
                            id: 'transaction.captured',
                            label: 'transaction.captured',
                        },
                        {
                            id: 'transaction.created',
                            label: 'transaction.created',
                        },
                        {
                            id: 'transaction.released',
                            label: 'transaction.released',
                        },
                    ]
                },
                {
                    id: 'locations',
                    label: 'Locations',
                    children: [
                        {
                            id: 'location.created',
                            label: 'location.created',
                        },
                        {
                            id: 'location.updated',
                            label: 'location.updated',
                        },
                        {
                            id: 'location.deleted',
                            label: 'location.deleted',
                        },
                    ]
                },
            ]
        }
    },

    props: {
        webhookId: {
            type: String,
            required: false,
        },
    },

    computed: {
        formEndpoint() {
            return this.webhookId
                ? `/api/webhooks/${this.webhookId}`
                : `/api/webhooks`
        },

        formMethod() {
            return this.webhookId
                ? 'patch'
                : 'post'
        },

        prepopulate() {
            return !!this.webhookId
        },
    },

    mounted() {
        this.form.secret = this.generateSecret()
    },

    methods: {
        prepopulated({ data }) {
            this.form.status = data.status === 'active'
                ? 1
                : 0
        },

        generateSecret(includeUppercase = true, includeLowercase = true, includeNumbers = true, includeSpecial = false) {
            const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
            const lowercase = 'abcdefghjkmnpqrstuvwxyz'
            const numbers = '23456789'
            const special = '!@#$%^&*()-_=+[]{}|;:,.<>?'

            // Combine selected character sets
            let chars = ''
            if (includeUppercase) chars += uppercase
            if (includeLowercase) chars += lowercase
            if (includeNumbers) chars += numbers
            if (includeSpecial) chars += special

            // Default to alphanumeric if no character sets selected
            if (chars === '') {
                chars = uppercase + lowercase + numbers
            }

            // Generate section with specified length
            const generateSection = (length) => {
                let section = ''
                for (let i = 0; i < length; i++) {
                    const randomIndex = Math.floor(Math.random() * chars.length)
                    section += chars[randomIndex]
                }

                return section
            };

            // Generate random section lengths that sum to 20 characters (for consistency with 5 sections of 4)
            const generateRandomSectionLengths = (totalLength, sectionsCount, minLength, maxLength) => {
                // Start with minimum lengths for all sections
                const lengths = Array(sectionsCount).fill(minLength)
                let remainingChars = totalLength - (minLength * sectionsCount)

                // Distribute remaining characters randomly
                while (remainingChars > 0) {
                    // Pick a random section
                    const sectionIndex = Math.floor(Math.random() * sectionsCount)

                    // If this section can get one more character without exceeding max length
                    if (lengths[sectionIndex] < maxLength) {
                        lengths[sectionIndex]++
                        remainingChars--
                    }
                }

                return lengths
            }

            // Generate 4-5 sections with randomized lengths between 2-6 characters
            // Total length will be 20 characters (same as 5 sections of 4 characters)
            const sectionCount = Math.random() < 0.5 ? 4 : 5
            const minSectionLength = 2
            const maxSectionLength = 6
            const totalCharLength = 20

            const sectionLengths = generateRandomSectionLengths(
                totalCharLength,
                sectionCount,
                minSectionLength,
                maxSectionLength
            )

            // Generate sections with the random lengths
            const sections = sectionLengths.map(length => generateSection(length))

            // Join sections with hyphens
            return sections.join('-')
        },

        formSuccess() {
            this.$emit(this.webhookId ? 'webhook-updated' : 'webhook-created')
            this.destroy()
        },

        formatData(form) {
            form.status = form.status === 1
                ? 'active'
                : 'inactive'

            return form
        },
    },
}
</script>

<style lang="scss" scoped>
.helper-text {
    margin-top: 0.313rem;
    font-size: 0.75rem;
    color: #2d2d2d;
    cursor: pointer;

    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &.subtitle {
        cursor: default;
        opacity: 0.8;
    }

    span:hover {
        color: #1f1f1f;
        text-decoration: underline;
    }

    i.icon {
        font-size: 1.188rem;
        position: relative;
        top: 0.313rem;
    }
}
</style>
